import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {AuthService, UserProfile} from '../service/auth.service';

interface AuthContextType {
    token: string | null;
    user: UserProfile | null;
    login: (email: string, password: string) => Promise<void>;
    initiateLinkedInLogin: () => Promise<void>;
    initiateGoogleLogin: () => Promise<void>;
    logout: () => void;
    setToken: (token: string | null) => void;
    refreshProfile: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [token, setTokenState] = useState<string | null>(localStorage.getItem('access_token'));
    const [user, setUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(false);

    const setToken = useCallback((newToken: string | null) => {
        if (newToken) {
            localStorage.setItem('access_token', newToken);
        } else {
            localStorage.removeItem('access_token');
        }
        setTokenState(newToken);
    }, []);

    const refreshProfile = useCallback(async () => {
        if (!token) {
            setUser(null);
            return;
        }

        try {
            setLoading(true);
            const profile = await AuthService.getProfile();
            setUser(profile);
        } catch (error) {
            // Handle silently - user will be prompted to login if needed
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Load user profile when token changes
    useEffect(() => {
        if (token) {
            refreshProfile();
        } else {
            setUser(null);
        }
    }, [token, refreshProfile]);

    const login = useCallback(async (email: string, password: string) => {
        const data = await AuthService.login(email, password);
        setToken(data.access_token);
    }, [setToken]);

    const initiateLinkedInLogin = useCallback(async () => {
        const { url, state } = await AuthService.getLinkedInAuthUrl();
        localStorage.setItem('oauth_state', state);
        window.location.href = url;
    }, []);

    const initiateGoogleLogin = useCallback(async () => {
        try {
            const { url } = await AuthService.getGoogleAuthUrl();
            window.location.href = url;
        } catch (error) {
            throw error;
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('access_token');
        setTokenState(null);
        setUser(null);
    }, []);

    const value = useMemo(() => ({
        token,
        user,
        login,
        logout,
        initiateLinkedInLogin,
        initiateGoogleLogin,
        setToken,
        refreshProfile,
        loading
    }), [token, user, login, logout, initiateLinkedInLogin, initiateGoogleLogin, setToken, refreshProfile, loading]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};